'use server';
import { api, ApiError, publicPost } from '@/lib/api';
import type {
  ClinicBookingEditorState,
  ClinicBookingMutation,
  ClinicBookingRequest,
} from '@/lib/clinic-booking';

/** Codes of the phone check the page explains on their own; everything else maps to the general keys. */
const CODE_ERRORS = new Set([
  'booking_code_invalid',
  'booking_code_expired',
  'booking_code_attempts',
  'booking_code_wait',
  'booking_phone_limit',
  'booking_open_limit',
  'booking_ip_limit',
  'booking_captcha_failed',
  'booking_phone_check_unavailable',
]);
function errorKey(error: unknown) {
  if (error instanceof ApiError && error.code && CODE_ERRORS.has(error.code)) return error.code;
  return error instanceof ApiError && error.status === 409
    ? 'conflict'
    : error instanceof ApiError && error.status === 429
      ? 'rateLimit'
      : 'failed';
}
export async function loadClinicBooking(clinicId: string) {
  try {
    return {
      data: await api.get<ClinicBookingEditorState>(
        `/clinics/${encodeURIComponent(clinicId)}/booking`,
      ),
    };
  } catch (error) {
    return { error: errorKey(error) };
  }
}
export async function changeClinicBooking(clinicId: string, input: ClinicBookingMutation) {
  const root = `/clinics/${encodeURIComponent(clinicId)}/booking`;
  try {
    let data: ClinicBookingEditorState;
    if (input.action === 'save')
      data = await api.post(`${root}/profiles`, {
        ...input.content,
        membershipId: input.membershipId,
        version: input.version,
      });
    else if (input.action === 'publish' || input.action === 'unpublish')
      data = await api.post(`${root}/profiles/${encodeURIComponent(input.id)}/publication`, {
        version: input.version,
        action: input.action,
      });
    else if (input.action === 'slot')
      data = await api.post(`${root}/profiles/${encodeURIComponent(input.id)}/slots`, {
        date: input.date,
        time: input.time,
      });
    else data = await api.patch(`${root}/slots/${encodeURIComponent(input.id)}/close`, {});
    return { data };
  } catch (error) {
    return { error: errorKey(error) };
  }
}
export async function loadClinicBookingRequests(clinicId: string) {
  try {
    return {
      data: await api.get<ClinicBookingRequest[]>(
        `/clinics/${encodeURIComponent(clinicId)}/booking/requests`,
      ),
    };
  } catch (error) {
    return { error: errorKey(error) };
  }
}
export async function decideClinicBookingRequest(
  clinicId: string,
  requestId: string,
  status: 'accepted' | 'rejected',
) {
  try {
    return {
      data: await api.patch<ClinicBookingRequest[]>(
        `/clinics/${encodeURIComponent(clinicId)}/booking/requests/${encodeURIComponent(requestId)}`,
        { status },
      ),
    };
  } catch (error) {
    return { error: errorKey(error) };
  }
}
export async function sendClinicBookingRequest(
  slug: string,
  profileId: string,
  slotId: string,
  input: {
    idempotencyKey: string;
    patientName: string;
    phone: string;
    email?: string;
    serviceId?: string;
    consent: boolean;
    consentVersion: string;
    turnstileToken?: string;
  },
) {
  try {
    return {
      data: await publicPost<BookingStart>(
        `/sites/${encodeURIComponent(slug)}/booking/${encodeURIComponent(profileId)}/slots/${encodeURIComponent(slotId)}/requests`,
        input,
      ),
    };
  } catch (error) {
    return { error: errorKey(error) };
  }
}

/** The API sends a WhatsApp code first; `pending` only when the clinic runs without the check. */
export type BookingStart =
  | { status: 'verify'; verificationId: string; phoneEnding: string; resendAfter: string }
  | { status: 'pending'; id: string };

export async function confirmBookingCode(slug: string, verificationId: string, code: string) {
  try {
    return {
      data: await publicPost<{ id: string; status: string }>(
        `/sites/${encodeURIComponent(slug)}/booking/verifications/${encodeURIComponent(verificationId)}/confirm`,
        { code },
      ),
    };
  } catch (error) {
    return { error: errorKey(error) };
  }
}

export async function resendBookingCode(slug: string, verificationId: string) {
  try {
    return {
      data: await publicPost<{ resendAfter: string }>(
        `/sites/${encodeURIComponent(slug)}/booking/verifications/${encodeURIComponent(verificationId)}/resend`,
        {},
      ),
    };
  } catch (error) {
    return { error: errorKey(error) };
  }
}
